import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { FullscreenModal } from './fullscreen-modal';

interface VideoPreviewProps {
  fileUrl: string;
  fileName: string;
}

export const VideoPreview: React.FC<VideoPreviewProps> = ({ fileUrl, fileName }) => {
  const { t } = useTranslation();
  const [showPlayer, setShowPlayer] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const getFileIcon = () => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'mp4':
        return 'mdi:file-video';
      case 'webm':
        return 'mdi:file-video';
      case 'avi':
        return 'mdi:file-video';
      case 'mov':
        return 'mdi:file-video';
      default:
        return 'mdi:file-video';
    }
  };

  const getFileTypeLabel = () => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'mp4':
        return t('forms.mp4Video');
      case 'webm':
        return t('forms.webmVideo');
      case 'avi':
        return t('forms.aviVideo');
      case 'mov':
        return t('forms.movVideo');
      default:
        return t('forms.videoFile');
    }
  };

  const getVideoType = () => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'mp4':
        return 'video/mp4';
      case 'webm':
        return 'video/webm';
      case 'avi':
        return 'video/avi';
      case 'mov':
        return 'video/quicktime';
      default:
        return 'video/mp4';
    }
  };

  const handlePlayerToggle = () => {
    setShowPlayer(!showPlayer);
  };

  const handleFullscreenClick = () => {
    setIsFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          <Icon icon={getFileIcon()} className="text-4xl text-indigo-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate" title={fileName}>
            {fileName}
          </p>
          <p className="text-xs text-gray-500">{getFileTypeLabel()}</p>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={handlePlayerToggle}
            className="flex-shrink-0 px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm hover:bg-blue-200 transition-colors"
          >
            {showPlayer ? t('forms.hidePlayer') : t('forms.play')}
          </button>
          <button
            type="button"
            onClick={handleFullscreenClick}
            className="flex-shrink-0 px-3 py-1 bg-purple-100 text-purple-700 rounded-md text-sm hover:bg-purple-200 transition-colors"
            title={t('forms.openInFullscreen')}
          >
            <Icon icon="mdi:fullscreen" className="text-sm" />
          </button>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-md text-sm hover:bg-indigo-200 transition-colors"
          >
            {t('forms.download')}
          </a>
        </div>
      </div>
      
      {showPlayer && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900">{t('forms.videoPlayer')}</h4>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <Icon icon="mdi:play" className="text-sm" />
                <span>{t('forms.clickPlayToWatch')}</span>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded border border-gray-200 p-4">
              <video controls className="w-full max-h-64 rounded-lg">
                <source src={fileUrl} type={getVideoType()} />
                <source src={fileUrl} type="video/mp4" />
                <source src={fileUrl} type="video/webm" />
                Your browser does not support the video element.
              </video>
              <div className="mt-2 text-center">
                <p className="text-xs text-gray-500">{getFileTypeLabel()}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Modal */}
      <FullscreenModal
        isOpen={isFullscreen}
        onClose={handleCloseFullscreen}
        title={`${getFileTypeLabel()} - ${fileName}`}
      >
        <div className="h-full flex flex-col items-center justify-center bg-gray-100">
          <div className="w-full max-w-4xl">
            <video controls className="w-full h-auto max-h-full rounded-lg shadow-lg">
              <source src={fileUrl} type={getVideoType()} />
              <source src={fileUrl} type="video/mp4" />
              <source src={fileUrl} type="video/webm" />
              Your browser does not support the video element.
            </video>
            <div className="mt-4 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{getFileTypeLabel()}</h3>
              <p className="text-gray-600 mb-4">{fileName}</p>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg text-lg hover:bg-indigo-700 transition-colors"
              >
                <Icon icon="mdi:download" className="mr-2" />
                {t('forms.download')}
              </a>
            </div>
          </div>
        </div>
      </FullscreenModal>
    </div>
  );
};


